import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { ChartTooltip } from "@/components/common/ChartTooltip";
import { axisTick, chartTheme, LiveDot } from "@/lib/chartTheme";
import { fmtNum, toOslo } from "@/lib/format";

/** Round the axis maximum up to a "nice" step so ticks read 0 / 500 / 1 000 … instead of 550 / 1 100 … */
const niceMax = (max) => {
  if (!max || max <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(max));
  const step = max / mag < 2 ? mag / 4 : max / mag < 5 ? mag / 2 : mag;
  return Math.ceil(max / step) * step;
};

/** Daily energy bars (portfolio or plant). rows: [{date, energy_mwh}] */
export const DailyEnergyChart = ({ rows = [], height = 220, isDark = false, testId, markLastIncomplete = false }) => {
  const t = chartTheme(isDark);
  const data = rows.map((r) => ({ x: format(toOslo(r.date), "EEE d.", { locale: nb }), full: format(toOslo(r.date), "EEEE d. MMM", { locale: nb }), energy: r.energy_mwh }));
  const yMax = niceMax(Math.max(0, ...data.map((d) => d.energy || 0)));
  return (
    <div style={{ height }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 4, left: -6, bottom: 0 }} barCategoryGap="28%">
          <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 6" />
          <XAxis dataKey="x" tick={axisTick(t)} tickLine={false} axisLine={false} tickMargin={10} interval={data.length > 12 ? Math.ceil(data.length / 8) : 0} />
          <YAxis domain={[0, yMax]} tickCount={5} allowDecimals={false} tick={axisTick(t)} tickLine={false} axisLine={false} tickFormatter={(v) => fmtNum(v, 0)} width={50} />
          <Tooltip cursor={{ fill: t.cursor, opacity: 0.05 }} content={<ChartTooltip formatter={(v) => `${fmtNum(v, 1)} MWh`} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} />} />
          <Bar dataKey="energy" name="Produksjon" radius={[3, 3, 0, 0]} maxBarSize={44}>
            {data.map((_, i) => {
              const last = i === data.length - 1;
              return <Cell key={i} fill={t.primary} fillOpacity={last && markLastIncomplete ? 0.3 : 0.9} stroke={last && markLastIncomplete ? t.primary : "none"} strokeWidth={last && markLastIncomplete ? 1.5 : 0} strokeDasharray={last && markLastIncomplete ? "4 3" : undefined} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Hourly power area (last 24h). rows: [{ts, power_mw}] */
export const HourlyPowerChart = ({ rows = [], height = 200, isDark = false, capacity, testId }) => {
  const t = chartTheme(isDark);
  const data = rows.map((r) => ({ x: format(toOslo(r.ts), "HH.mm"), power: r.power_mw }));
  const last = data[data.length - 1];
  return (
    <div style={{ height }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 12, right: 12, left: -6, bottom: 0 }}>
          <defs>
            <linearGradient id="fillHourly" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={t.primary} stopOpacity={0.3} />
              <stop offset="100%" stopColor={t.primary} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 6" />
          <XAxis dataKey="x" tick={axisTick(t)} tickLine={false} axisLine={false} interval={5} tickMargin={10} />
          <YAxis tick={axisTick(t)} tickLine={false} axisLine={false} tickFormatter={(v) => fmtNum(v, v < 10 ? 1 : 0)} width={46} domain={[0, capacity ? Math.ceil(capacity * 1.08 * 10) / 10 : "auto"]} />
          <Tooltip content={<ChartTooltip formatter={(v) => `${fmtNum(v, 2)} MW`} />} cursor={{ stroke: t.cursor, strokeOpacity: 0.25, strokeDasharray: "3 3" }} />
          <Area type="monotone" dataKey="power" name="Effekt" stroke={t.primary} strokeWidth={2.25} fill="url(#fillHourly)" dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: isDark ? "hsl(207 50% 10%)" : "#fff" }} />
          {last && last.power !== null && last.power !== undefined ? <ReferenceDot x={last.x} y={last.power} r={0} shape={(p) => <LiveDot {...p} color={t.live} />} ifOverflow="visible" /> : null}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Tiny sparkline for list rows (recharts based, kept for compatibility). */
export const Sparkline = ({ rows = [], dataKey = "power_mw", height = 32, width = 96, isDark = false }) => {
  const t = chartTheme(isDark);
  const data = rows.map((r) => ({ v: r[dataKey] }));
  return (
    <div style={{ height, width }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Area type="monotone" dataKey="v" stroke={t.primary} strokeWidth={1.5} fill={t.primary} fillOpacity={0.12} dot={false} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
